import PsychologyIcon from '@mui/icons-material/Psychology';
import { Chip, Stack } from '@mui/material';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { ChannelItemDirection } from '../../../../sic-lib/enums/ChannelItemDirection';
import { ChannelItemCall } from '../../../../sic-lib/types/ChannelItem';
import { splitCamelCase } from '../../../../sic-lib/utils/aiHelpers';
import { secondsToHumanReadable } from '../../../../sic-lib/utils/dateHelpers';
import { setDrawerTranscript } from '../../../../store/conversation';
import { useAppDispatch } from '../../../../store/hooks';
import DisplayPhoneNumber from '../../../Shared/DisplayPhoneNumber/DisplayPhoneNumber';

interface Props {
    config: ChannelItemCall;
}

export default React.memo(function ConversationListItemAudio({ config: channelItem }: Props) {
    const { classes, cx } = useStyles();
    const dispatch = useAppDispatch();

    const aiCallSummary = React.useMemo(() => {
        let aiObj: { [x: string]: string }[] = [];
        if (channelItem.aiTags) {
            Object.entries(channelItem.aiTags).forEach(([key, value]) => {
                if (value !== '' && value !== 'unknown')
                    aiObj.push({ [splitCamelCase(key)]: value });
            });
        }
        return aiObj;
    }, [channelItem.aiTags]);

    const handleBodyClick = () => {
        dispatch(
            setDrawerTranscript({
                channelItem: channelItem,
            }),
        );
    };

    return (
        <Stack
            className={classes.wrapper}
            onClick={handleBodyClick}
            flexDirection="column"
            justifyContent="flex-start"
            spacing={1}
        >
            <Typography component="span" variant="caption" display="block" noWrap>
                <DisplayPhoneNumber phoneNumber={channelItem.referenceNumber} />
                {!!channelItem.duration && (
                    <> &bull; {secondsToHumanReadable(channelItem.duration)}</>
                )}
                {channelItem.callStatus !== 'completed' && <> &bull; {channelItem.callStatus}</>}
            </Typography>
            {channelItem.callStatus === 'completed' && (
                <>
                    {channelItem.aiSentenceSummary && (
                        <Typography variant="body1" display="block">
                            {channelItem.aiSentenceSummary}
                        </Typography>
                    )}
                    {aiCallSummary.length > 0 && (
                        <Stack direction="column" spacing={1} flexWrap="wrap">
                            {aiCallSummary.map((item, index) => {
                                return (
                                    <Chip
                                        key={`${Object.keys(item)}-${index}`}
                                        label={`${Object.keys(item)}: ${Object.values(item)}`}
                                        size="small"
                                        className={cx(
                                            classes.chip,
                                            channelItem.direction === ChannelItemDirection.incoming
                                                ? classes.chipIn
                                                : classes.chipOut,
                                        )}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                    {channelItem.isRecordingDownloaded && (
                        <Typography
                            variant="caption"
                            display="block"
                            noWrap
                            className={classes.aiButton}
                        >
                            View Call Analysis <PsychologyIcon />
                        </Typography>
                    )}
                    {!channelItem.isRecordingDownloaded && (
                        <Typography variant="caption" display="block" noWrap>
                            No recording available
                        </Typography>
                    )}
                </>
            )}
        </Stack>
    );
});

const useStyles = makeStyles()((theme: Theme) => ({
    wrapper: {
        width: '100%',
        padding: theme.spacing(2),
        cursor: 'pointer',
    },
    chip: {
        cursor: 'pointer',
        boxShadow:
            '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12);',
    },
    aiButton: {
        marginTop: theme.spacing(1),
        fontStyle: 'italic',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'center',
        gap: '4px',
    },
    chipIn: {
        backgroundColor: theme.palette.conversationTimelinePaper.light,
        color: theme.palette.primary.contrastText,
    },
    chipOut: {
        backgroundColor: theme.palette.conversationTimelinePaper.main,
        color: theme.palette.primary.contrastText,
    },
}));
